import type { BlockField } from '@/blocks/domain/types';
import type { DocumentTypeVariableCategory } from '@/document-types/domain/types/DocumentType';

/**
 * WHAT A PANEL ROW IS CALLED — one rule, one place.
 *
 * design ruling 2026-09-08: a panel row's name is a wayfinding label — its job is to let someone
 * find that thing on the canvas. It is not a field, and editability is not the test:
 * "Description" is what that column is whether or not anyone can rename it.
 *
 * The precedence, first that exists:
 *   1. authored label — the field's `name`, its `label`, or the element's own authored text
 *   2. catalog label  — the human name for a token path, from the variable catalog
 *   3. raw token      — `{{customer.name}}` (outranks the type: six token rows all read
 *                        "Variable" — the raw token is what actually tells them apart)
 *   4. element type   — "Text", "Heading" — the fallback, not the answer
 *
 * Never invent a label for a path the catalog does not carry — step 3 exists so nothing has to.
 */

/** `{{path}}` — the token syntax `CLAUDE.md` defines under Variables and token resolution. */
const TOKEN_RE = /^\s*\{\{\s*([^}]+?)\s*\}\}\s*$/;

/** The path inside a token, or null when the string is not a bare token. */
export function tokenPath(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const m = TOKEN_RE.exec(value);
  return m?.[1] ?? null;
}

/**
 * The catalog's human label for a token path. Flat scan rather than an index: the catalog is a
 * handful of groups, rebuilt whenever the document type changes.
 */
export function catalogLabelFor(categories: DocumentTypeVariableCategory[] | undefined, path: string | null): string | undefined {
  if (!path || !categories?.length) return undefined;
  for (const category of categories) {
    const hit = category.variables?.find((v) => v.path === path);
    if (hit?.label) return hit.label;
  }
  return undefined;
}

/**
 * The row's name, by the precedence above. `typeLabel` is passed in rather than computed here; callers
 * pass `fieldStyleLabel(style)` — the one place the element-type wording lives (never the title-cased
 * wire value: "rich_text" is "Text", not "Rich Text").
 */
export function panelRowName(
  field: Pick<BlockField, 'label' | 'value'> & { name?: string },
  options: {
    categories?: DocumentTypeVariableCategory[];
    authoredText?: string;
    typeLabel: string;
    /** Step 3 on/off. The canvas selection tab turns it off (DND-08): a tab never shows `{{path}}`. */
    rawToken?: boolean;
  }
): string {
  const authored = field.name?.trim() || field.label?.trim() || options.authoredText?.trim();
  if (authored) return authored;

  const path = tokenPath(field.value);
  if (path) {
    const catalog = catalogLabelFor(options.categories, path);
    if (catalog) return catalog;
    if (options.rawToken !== false) return `{{${path}}}`;
  }

  return options.typeLabel;
}
